import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot, Trash2, Save, Plus, X as XIcon } from "lucide-react";
import * as Tabs from "@radix-ui/react-tabs";
import { BridgeApi, RevisionsApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { JsonDiff } from "../components/JsonDiff";
import { MountsEditor } from "../components/MountsEditor";
import { relativeTime } from "../lib/format";
import { cn } from "../lib/cn";
import type { Agent, Persona } from "../lib/types";

interface AgentEntry { name: string; agent: Agent; usedBy: string[] }

const NEW_AGENT: Agent = { command: "", env: {} };

export default function Agents() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(null);

  const { data: cfg, isLoading } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });

  const entries: AgentEntry[] = useMemo(() => {
    if (!cfg) return [];
    const agents = cfg.agents ?? {};
    const personas = cfg.personas ?? {};
    const usedByMap: Record<string, string[]> = {};
    for (const [pName, p] of Object.entries(personas) as Array<[string, Persona]>) {
      if (!p.agent) continue;
      if (!usedByMap[p.agent]) usedByMap[p.agent] = [];
      usedByMap[p.agent].push(pName);
    }
    return Object.entries(agents).map(([name, agent]) => ({ name, agent, usedBy: usedByMap[name] ?? [] }));
  }, [cfg]);


  const deleteMut = useMutation({
    mutationFn: BridgeApi.deleteAgent,
    onSuccess: () => {
      setSelected(null);
      qc.invalidateQueries({ queryKey: qk.bridge.config });
      toast.success("Agent deleted");
    },
    onError: (err) => toast.error("Delete failed", String(err)),
  });

  if (isLoading) return <CenteredSpinner />;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Agents"
        icon={Bot}
        subtitle={`${entries.length} agent configs`}
        actions={
          <Button size="sm" onClick={() => setSelected("__new__")}>
            <Plus className="size-3.5" />
            New agent
          </Button>
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<AgentEntry>
          items={entries}
          itemKey={(e) => e.name}
          selectedId={selected}
          onSelect={setSelected}
          itemHeight={68}
          emptyState={<EmptyState icon={<Bot className="size-8" />} title="No agents yet" />}
          renderItem={({ item }) => <AgentRow entry={item} />}
          renderCreating={() => (
            <AgentEditor
              key="__new__"
              entryName=""
              agent={NEW_AGENT}
              usedBy={[]}
              isCreating
              existingNames={entries.map((e) => e.name)}
              onSaved={(name) => {
                qc.invalidateQueries({ queryKey: qk.bridge.config });
                setSelected(name);
              }}
              onDelete={() => { /* no-op for create */ }}
              onClose={() => setSelected(null)}
            />
          )}
          renderDetail={(item) => (
            <AgentEditor
              key={item.name}
              entryName={item.name}
              agent={item.agent}
              usedBy={item.usedBy}
              isCreating={false}
              existingNames={entries.map((e) => e.name)}
              onSaved={(name) => {
                qc.invalidateQueries({ queryKey: qk.bridge.config });
                setSelected(name);
              }}
              onDelete={() => {
                if (confirm(`Delete agent "${item.name}"?`)) {
                  deleteMut.mutate(item.name);
                }
              }}
              onClose={() => setSelected(null)}
            />
          )}
        />
      </div>
    </div>
  );
}

function AgentRow({ entry }: { entry: AgentEntry }) {
  const cmd = entry.agent.command.split("\n")[0].slice(0, 80);
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <div className="size-5 rounded bg-accent-subtle text-accent text-[10px] font-bold flex items-center justify-center">
          {entry.name.slice(0, 2).toUpperCase()}
        </div>
        <span className="text-sm font-medium text-fg truncate">{entry.name}</span>
        {entry.usedBy.length > 0 && (
          <span className="ml-auto text-[11px] text-fg-subtle">
            used by <span className="text-fg-muted">{entry.usedBy.length}</span>
          </span>
        )}
      </div>
      <div className="text-xs text-fg-muted font-mono truncate">{cmd || "(no command)"}</div>
    </div>
  );
}

function AgentEditor({
  entryName,
  agent,
  usedBy,
  isCreating,
  existingNames,
  onSaved,
  onDelete,
  onClose,
}: {
  entryName: string;
  agent: Agent;
  usedBy: string[];
  isCreating: boolean;
  existingNames: string[];
  onSaved: (name: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(entryName);
  const [draft, setDraft] = useState<Agent>(agent);
  const [envText, setEnvText] = useState(envToText(agent.env));
  const [tab, setTab] = useState<"edit" | "diff" | "history">("edit");

  useEffect(() => {
    setName(entryName);
    setDraft(agent);
    setEnvText(envToText(agent.env));
  }, [entryName, isCreating]);

  const dirty = useMemo(() => {
    return name !== entryName || JSON.stringify(draft) !== JSON.stringify(agent);
  }, [name, entryName, draft, agent]);

  const save = useMutation({
    mutationFn: async () => {
      if (!name) throw new Error("name is required");
      if (isCreating && existingNames.includes(name)) throw new Error(`agent "${name}" already exists`);
      const env = parseEnvText(envText);
      const payload: Agent = { ...draft, env };
      if (!isCreating && name !== entryName) {
        // True rename: single UPDATE — the FK ON UPDATE CASCADE rewires
        // every persona pointing at this agent automatically. Then we
        // save the (possibly-edited) body under the new name.
        await BridgeApi.renameAgent(entryName, name);
        await BridgeApi.saveAgent(name, payload);
      } else {
        await BridgeApi.saveAgent(name, payload);
      }
    },
    onSuccess: () => {
      toast.success(`Agent "${name}" saved`);
      onSaved(name);
    },
    onError: (err) => toast.error("Save failed", String(err)),
  });

  const after: Agent = { ...draft, env: parseEnvText(envText) };

  return (
    <Tabs.Root value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="flex-1 flex flex-col min-h-0">
      <div className="px-5 pt-4 pb-2 border-b border-border flex items-start justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-fg">{isCreating ? "New agent" : entryName}</h2>
          {usedBy.length > 0 && (
            <p className="text-xs text-fg-muted mt-1">
              used by{" "}
              {usedBy.map((p) => (
                <Pill key={p} tone="info" className="mr-1">{p}</Pill>
              ))}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isCreating && (
            <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete">
              <Trash2 className="size-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}><XIcon className="size-3.5" /></Button>
        </div>
      </div>

      <Tabs.List className="border-b border-border px-5 py-1.5 flex items-center gap-3 text-xs font-medium">
        <TabBtn value="edit">Edit</TabBtn>
        <TabBtn value="diff" disabled={!dirty}>Diff</TabBtn>
        {!isCreating && <TabBtn value="history">History</TabBtn>}
      </Tabs.List>

      <div className="flex-1 overflow-y-auto">
        <Tabs.Content value="edit" className="px-5 py-4 space-y-4 outline-none">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full font-mono" />
          </Field>
          <Field label="Command (shell)">
            <textarea
              value={draft.command}
              onChange={(e) => setDraft({ ...draft, command: e.target.value })}
              rows={6}
              className="w-full font-mono text-xs"
              placeholder="e.g. claude -p"
            />
          </Field>
          <Field label="Environment (KEY=VALUE per line)">
            <textarea
              value={envText}
              onChange={(e) => setEnvText(e.target.value)}
              rows={6}
              className="w-full font-mono text-xs"
              placeholder="ANTHROPIC_API_KEY=sk-..."
            />
            <p className="text-[10px] text-fg-subtle mt-1">
              Empty lines and lines starting with # are ignored.
            </p>
          </Field>
          {!isCreating && <AgentMounts agentName={entryName} />}
        </Tabs.Content>

        <Tabs.Content value="diff" className="px-5 py-4 outline-none">
          <div className="text-xs text-fg-muted mb-2">Pending changes to <code className="font-mono">routes.yaml</code>:</div>
          <JsonDiff before={agent} after={after} />
        </Tabs.Content>

        {!isCreating && (
          <Tabs.Content value="history" className="px-5 py-4 outline-none">
            <ObjectHistory objectType="agent" objectName={entryName} onReverted={() => onSaved(entryName)} />
          </Tabs.Content>
        )}
      </div>

      <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
        {dirty && <span className="mr-auto text-xs text-warning">unsaved changes</span>}
        <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || !name} loading={save.isPending}>
          <Save className="size-3.5" />
          {isCreating ? "Create" : "Save"}
        </Button>
      </div>
    </Tabs.Root>
  );
}

// Agent-level sandbox mounts — inherited by every persona linked to this agent.
function AgentMounts({ agentName }: { agentName: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["agent-mounts", agentName], queryFn: () => BridgeApi.agentMounts(agentName) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["agent-mounts", agentName] });
  return (
    <MountsEditor
      own={data?.mounts ?? []}
      inherited={data?.inherited ?? []}
      hint="agent tier — every persona on this agent inherits these; overrides global"
      onAdd={(path, mode) => BridgeApi.addAgentMount(agentName, path, mode).then(invalidate).catch((e) => toast.error("Add failed", String(e)))}
      onRemove={(path) => BridgeApi.removeAgentMount(agentName, path).then(invalidate).catch((e) => toast.error("Remove failed", String(e)))}
    />
  );
}

function TabBtn({ value, children, disabled }: { value: string; children: React.ReactNode; disabled?: boolean }) {
  return (
    <Tabs.Trigger
      value={value}
      disabled={disabled}
      className={cn(
        "h-7 px-2 rounded text-fg-muted hover:text-fg data-[state=active]:text-fg data-[state=active]:bg-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
      )}
    >
      {children}
    </Tabs.Trigger>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">{label}</span>
      {children}
    </label>
  );
}

function envToText(env: Record<string, string>): string {
  return Object.entries(env).map(([k, v]) => `${k}=${v}`).join("\n");
}

function parseEnvText(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const eq = line.indexOf("=");
    if (eq < 0) continue;
    out[line.slice(0, eq).trim()] = line.slice(eq + 1).trim();
  }
  return out;
}

// ─── Shared history component ──────────────────────────────────────────

export function ObjectHistory({
  objectType,
  objectName,
  onReverted,
}: {
  objectType: string;
  objectName: string;
  onReverted: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: revs = [], isLoading } = useQuery({
    queryKey: qk.revisions.list({ object_type: objectType, object_name: objectName }),
    queryFn: () => RevisionsApi.list({ object_type: objectType, object_name: objectName, limit: 50 }),
  });
  const revert = useMutation({
    mutationFn: RevisionsApi.revert,
    onSuccess: () => {
      toast.success("Reverted");
      qc.invalidateQueries({ queryKey: qk.bridge.config });
      qc.invalidateQueries({ queryKey: qk.revisions.list({ object_type: objectType, object_name: objectName }) });
      onReverted();
    },
  });

  if (isLoading) return <CenteredSpinner />;
  if (revs.length === 0) {
    return <div className="text-sm text-fg-muted text-center py-6">No prior revisions.</div>;
  }
  return (
    <ul className="panel divide-y divide-border">
      {revs.map((r) => {
        const before = r.before ? JSON.parse(r.before) : null;
        const after = r.after ? JSON.parse(r.after) : null;
        return (
          <li key={r.id} className="p-3 text-xs">
            <div className="flex items-center gap-2">
              <Pill tone={r.action === "delete" ? "danger" : r.action === "create" ? "success" : "warning"}>{r.action}</Pill>
              <span className="text-fg-muted">{relativeTime(r.at)}</span>
              <span className="ml-auto font-mono text-fg-subtle">by {r.actor}</span>
            </div>
            <details className="mt-2">
              <summary className="text-fg-muted cursor-pointer hover:text-fg">show diff</summary>
              <div className="mt-2"><JsonDiff before={before} after={after} /></div>
            </details>
            <div className="mt-2 flex justify-end">
              <Button size="sm" variant="secondary" onClick={() => revert.mutate(r.id)} loading={revert.isPending}>
                Revert to before
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
